import React, { useState, useCallback, useEffect, useRef } from "react";
import { Transition } from "history";
import { useBlocker } from "./blocker";
import { Modal } from "../shared-components/Modal";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import { PreventLeaveContext, BlockerRegistration } from "./usePreventLeave";

export function PreventLeaveProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [registrations, setRegistrations] = useState<
    Map<string, BlockerRegistration>
  >(new Map());
  const [showModal, setShowModal] = useState(false);
  const [pendingTransition, setPendingTransition] = useState<Transition | null>(
    null
  );

  const isBlocking = registrations.size > 0;
  const registrationsRef = useRef(registrations);
  registrationsRef.current = registrations;

  const register = useCallback((registration: BlockerRegistration) => {
    setRegistrations((prev) => {
      const next = new Map(prev);
      next.set(registration.id, registration);
      return next;
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setRegistrations((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (registrationsRef.current.size > 0) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!isBlocking) {
      setPendingTransition(null);
      setShowModal(false);
    }
  }, [isBlocking]);

  useBlocker((tx: Transition) => {
    if (registrationsRef.current.size > 0) {
      setPendingTransition(tx);
      setShowModal(true);
    } else {
      tx.retry();
    }
  }, isBlocking);

  const handleLeavePage = useCallback(() => {
    const transitionToRetry = pendingTransition;
    setShowModal(false);
    setPendingTransition(null);

    registrationsRef.current.forEach((registration) => {
      registration.onLeave();
    });

    if (transitionToRetry) {
      setTimeout(() => {
        transitionToRetry.retry();
      }, 0);
    }
  }, [pendingTransition]);

  const handleStayOnPage = useCallback(() => {
    setShowModal(false);
    setPendingTransition(null);
  }, []);

  const getModalContent = () => {
    const regs = Array.from(registrations.values());
    if (regs.length === 0) return { title: "", message: "" };

    if (regs.length === 1) {
      return { title: regs[0].title, message: regs[0].message };
    }

    return {
      title: "Unsaved Changes",
      message:
        "You have unsaved changes and active timers. If you leave this page, all changes will be lost and timers will be stopped.",
    };
  };

  const { title, message } = getModalContent();

  return (
    <PreventLeaveContext.Provider value={{ register, unregister, isBlocking }}>
      {children}
      <Modal
        open={showModal}
        onClose={handleStayOnPage}
        title={title}
        width="w-full max-w-md"
      >
        <div className="py-4">
          <p className="text-sm text-gray-700 mb-4">{message}</p>
          <div className="flex justify-end gap-3">
            <Button
              text="Stay on Page"
              colorType={ButtonColorTypes.cancel}
              onClick={handleStayOnPage}
            />
            <Button
              text="Leave Page"
              colorType={ButtonColorTypes.secondary}
              onClick={handleLeavePage}
            />
          </div>
        </div>
      </Modal>
    </PreventLeaveContext.Provider>
  );
}
